import React, { FC, useState } from 'react';
import { Cell, Pie, PieChart, ResponsiveContainer, Sector, Tooltip } from 'recharts';
import { useQuery } from '@apollo/client';
import { netCancellationStatisticsQuery, totalCancellationsQuery } from '../../../../graph/dashboard';
import { Paper, Typography, Tooltip as FormulaTooltip, useTheme } from '@material-ui/core';
import Box from '@material-ui/core/Box';
import { getNetCancellationBreakdown } from './netCancellationRate';
import type { NetCancellationStatisticsQueryData } from '@/types/NetCancellationStatistics';
import ChartLegend from './charts/ChartLegend';

const COLORS = ['#00A7F7', '#FFBB28', '#485796', '#2B3C87', '#44AABF', '#FFD166', '#FF6F61', '#3D9970', '#F38181'];

export const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <Paper>
        <Box p={2}>
          <Typography gutterBottom>{payload[0].name}</Typography>
          {payload.map((p, i) => (
            <>
              <Box key={i} display="flex" alignItems="center" style={{ width: '16ch' }}>
                <Typography variant="h4">{`${Math.round(p.value)} case${
                  Math.round(p.value) % 10 == 1 ? '' : 's'
                }`}</Typography>
              </Box>
            </>
          ))}
        </Box>
      </Paper>
    );
  }

  return null;
};

const ProcedureCancellationChart: FC<{ filter: any }> = ({ filter }) => {
  const theme = useTheme();
  const [rateHover, setRateHover] = useState(false);
  const {
    data: fetchedData,
    loading,
    error,
  } = useQuery(totalCancellationsQuery, {
    variables: { filter },
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  });

  const netQuery = useQuery<NetCancellationStatisticsQueryData>(netCancellationStatisticsQuery, {
    variables: { filter },
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  });
  const reasonsReady = !loading && !error && Array.isArray(fetchedData?.totalCancellations?.data);
  const data = reasonsReady ? fetchedData.totalCancellations.data.map(e => ({ name: e.name, value: e.value })) : [];
  const total = reasonsReady ? Math.round(data.reduce((acc, cur) => acc + cur.value, 0)) : null;
  const breakdown =
    netQuery.loading || netQuery.error ? null : getNetCancellationBreakdown(netQuery.data?.netCancellationStatistics);
  // Separate reads can briefly disagree while the schedule changes. Never show
  // a formula whose cancellation count differs from the reasons in this chart.
  const rate = reasonsReady && breakdown && breakdown.cancelledCases === total ? breakdown.rate : null;
  const rateLabel = rate == null ? '—' : `${rate.toFixed(1)}%`;
  const formula = (
    <Box>
      <Typography variant="caption" component="div">
        (Cancelled cases − Rescheduled cases) / Total scheduled cases × 100
      </Typography>
      {rate != null && breakdown && (
        <Typography variant="caption" component="div">
          ({total} − {breakdown.rescheduledCases}) / {breakdown.totalScheduledCases} × 100 = {rateLabel}
        </Typography>
      )}
      <Typography variant="caption" component="div">
        Rescheduled: cancellations covered by non-cancelled appointments for the same patient, in the selected period or
        later.
      </Typography>
    </Box>
  );
  const renderCenter = props => {
    const { cx, cy, innerRadius, outerRadius, startAngle, endAngle, fill } = props;
    const textScale = Math.min(1, innerRadius / 80);
    return (
      <g>
        <Sector {...{ cx, cy, innerRadius, outerRadius, startAngle, endAngle, fill }} />
        <text
          pointerEvents="none"
          x={cx}
          y={cy - 12 * textScale}
          fontSize={Math.max(20, 28 * textScale)}
          textAnchor="middle"
          fill={theme.palette.text.primary}
        >
          {total == null ? '—' : total.toLocaleString('en-US')}
        </text>
        <text
          pointerEvents="none"
          x={cx}
          y={cy + 16 * textScale}
          fontSize={Math.max(11, 14 * textScale)}
          fontWeight={500}
          textAnchor="middle"
          fill={theme.palette.primary.main}
        >
          {loading ? 'Loading…' : error ? 'Unable to load' : 'Cancellations'}
        </text>
        <foreignObject
          x={cx - innerRadius}
          y={cy + 28 * textScale}
          width={innerRadius * 2}
          height={24}
          style={{ overflow: 'visible', pointerEvents: 'none' }}
        >
          <Box textAlign="center">
            <FormulaTooltip title={formula} arrow placement="bottom" enterTouchDelay={0}>
              <Typography
                component="span"
                variant="caption"
                tabIndex={0}
                onMouseEnter={() => setRateHover(true)}
                onMouseLeave={() => setRateHover(false)}
                style={{
                  cursor: 'help',
                  pointerEvents: 'auto',
                  color: theme.palette.text.primary,
                  whiteSpace: 'nowrap',
                  fontSize: Math.max(10, 12 * textScale),
                }}
              >
                Net rate: {rateLabel}
              </Typography>
            </FormulaTooltip>
          </Box>
        </foreignObject>
      </g>
    );
  };

  return (
    <Box width="100%">
      <ResponsiveContainer width="100%" height="auto" aspect={1}>
        <PieChart>
          <Pie
            startAngle={90}
            endAngle={-270}
            dataKey="value"
            data={data.length ? data : [{ name: '', value: 1 }]}
            innerRadius="50%"
            outerRadius="70%"
            activeShape={renderCenter}
            activeIndex={0}
            isAnimationActive={false}
            fill={theme.palette.action.disabledBackground}
          >
            {data.map((entry, index) => (
              <Cell
                stroke="#1A2553"
                strokeWidth={3}
                style={{ outline: 'none' }}
                key={`cell-${index}`}
                fill={COLORS[index % COLORS.length]}
              />
            ))}
          </Pie>
          <Tooltip
            content={({ active, payload, label }) => (
              <CustomTooltip active={active && data.length > 0 && !rateHover} payload={payload} label={label} />
            )}
            cursor={{ fill: 'rgba(255,255,255,0.15)' }}
            contentStyle={{
              backgroundColor: theme.palette.background.paper,
              borderRadius: 4,
              border: 0,
            }}
          />
        </PieChart>
      </ResponsiveContainer>
      {/* Keep wrapping reasons outside the plot so narrow panels retain a readable donut. */}
      <ChartLegend
        payload={data.map((entry, index) => ({ value: entry.name, color: COLORS[index % COLORS.length] }))}
      />
    </Box>
  );
};

export default ProcedureCancellationChart;
